/**
 * Карта v3 — порты.
 *
 * Отвечает за требование «несколько подключений к одному устройству»:
 * кабели не сваливаются в центр корпуса, а занимают отдельные точки на
 * периметре, входят перпендикулярно и не меняются местами при перерисовке.
 *
 * Порядок мест на стороне определяется проекцией цели на ось этой стороны —
 * поэтому кабели не перекрещиваются сразу у корпуса. Порядок стабилен:
 * одинаковый вход даёт одинаковый выход.
 */
import { PITCH, STUB } from './const';
import { clamp } from './math';

const SIDE_N = 'N', SIDE_E = 'E', SIDE_S = 'S', SIDE_W = 'W';

/** Точка на периметре: side + смещение t в диапазоне [-1..1] от центра стороны. */
export function portPoint(node, side, t) {
  const halfW = node.w / 2, halfH = node.h / 2;
  const ccx = node.x + halfW, ccy = node.y + halfH;
  switch (side) {
    case SIDE_N: return { x: ccx + t * halfW, y: node.y };
    case SIDE_S: return { x: ccx + t * halfW, y: node.y + node.h };
    case SIDE_W: return { x: node.x, y: ccy + t * halfH };
    default:     return { x: node.x + node.w, y: ccy + t * halfH };
  }
}

export function sideNormal(side) {
  if (side === SIDE_N) return { x: 0, y: -1 };
  if (side === SIDE_S) return { x: 0, y: 1 };
  if (side === SIDE_W) return { x: -1, y: 0 };
  return { x: 1, y: 0 };
}

/** Точка вылета: отходим от корпуса перпендикулярно, чтобы трасса не липла к нему. */
export function escapePoint(p, side, stub = STUB) {
  const n = sideNormal(side);
  return { x: p.x + n.x * stub, y: p.y + n.y * stub };
}

/**
 * Сторона корпуса, обращённая к цели.
 * Сравниваем не углы, а перекрытие проекций: если цель лежит «над» корпусом
 * по вертикали и по горизонтали пересекается — берём N/S, иначе E/W.
 * Так вход получается перпендикулярным и коротким, без диагональных заходов.
 */
export function pickSide(node, tx, ty) {
  const ccx = node.x + node.w / 2, ccy = node.y + node.h / 2;
  const dx = tx - ccx, dy = ty - ccy;

  const overlapX = tx > node.x - 4 && tx < node.x + node.w + 4;
  const overlapY = ty > node.y - 4 && ty < node.y + node.h + 4;

  if (overlapX && !overlapY) return dy < 0 ? SIDE_N : SIDE_S;
  if (overlapY && !overlapX) return dx < 0 ? SIDE_W : SIDE_E;

  // нормируем на половины габаритов — у широких корпусов бок «дешевле»
  const nx = Math.abs(dx) / (node.w / 2 || 1);
  const ny = Math.abs(dy) / (node.h / 2 || 1);
  if (nx >= ny) return dx < 0 ? SIDE_W : SIDE_E;
  return dy < 0 ? SIDE_N : SIDE_S;
}

/**
 * Раскладка мест на одной стороне.
 * Кабели идут в порядке проекции цели: тот, чья цель левее, займёт место левее.
 * Шаг PITCH, но если связей больше, чем помещается, шаг ужимается — места
 * хватит всегда, просто станет теснее.
 */
function seatOffsets(count, extent) {
  if (count <= 0) return [];
  if (count === 1) return [0];
  const usable = Math.max(extent - 16, 16);          // поля по краям корпуса
  const pitch = Math.min(PITCH, usable / (count - 1));
  const span = pitch * (count - 1);
  const half = extent / 2 || 1;
  const out = [];
  for (let i = 0; i < count; i++) {
    out.push(clamp((-span / 2 + i * pitch) / half, -0.92, 0.92));
  }
  return out;
}

/**
 * Вычисляет порты для всех связей сцены.
 * Вызывается перед трассировкой; результат кладётся в edge.aPt/bPt/aSide/bSide.
 *
 * @param s      сцена
 * @param anchor функция (edge, end) -> {x,y} — куда «смотрит» этот конец.
 *               Обычно центр противоположного узла, но если у связи есть
 *               ручной изгиб, смотрим на первую точку изгиба: тогда кабель
 *               выходит из корпуса в сторону, которую задал пользователь.
 */
export function computePorts(s, anchor) {
  const perNode = new Map();

  const push = (devId, item) => {
    if (!perNode.has(devId)) perNode.set(devId, []);
    perNode.get(devId).push(item);
  };

  for (const e of s.edges.values()) {
    const A = s.nodes.get(e.a), B = s.nodes.get(e.b);
    if (!A || !B || A.hidden || B.hidden) { e.aPt = e.bPt = null; continue; }

    const aTarget = anchor(e, 'a');
    const bTarget = anchor(e, 'b');

    const aSide = e.aPort?.mode === 'pinned' && e.aPort.side
      ? e.aPort.side : pickSide(A, aTarget.x, aTarget.y);
    const bSide = e.bPort?.mode === 'pinned' && e.bPort.side
      ? e.bPort.side : pickSide(B, bTarget.x, bTarget.y);

    push(e.a, { e, end: 'a', side: aSide, node: A, target: aTarget, seat: e.aSeat || 0 });
    push(e.b, { e, end: 'b', side: bSide, node: B, target: bTarget, seat: e.bSeat || 0 });
  }

  for (const [devId, list] of perNode) {
    const node = s.nodes.get(devId);
    if (!node) continue;

    const bySide = { N: [], E: [], S: [], W: [] };
    for (const it of list) bySide[it.side].push(it);

    for (const side of [SIDE_N, SIDE_E, SIDE_S, SIDE_W]) {
      const arr = bySide[side];
      if (!arr.length) continue;

      const horizontal = side === SIDE_N || side === SIDE_S;
      // порядок вдоль стороны — по проекции цели, чтобы кабели не перекрещивались
      arr.sort((p, q) => {
        const pv = horizontal ? p.target.x : p.target.y;
        const qv = horizontal ? q.target.x : q.target.y;
        if (pv !== qv) return pv - qv;
        return p.seat - q.seat;                       // ничья — стабильный порядок
      });

      const extent = horizontal ? node.w : node.h;
      const offs = seatOffsets(arr.length, extent);

      arr.forEach((it, i) => {
        // ручное закрепление позиции: пользователь сам поставил порт
        const cfg = it.e[it.end === 'a' ? 'aPort' : 'bPort'];
        const t = cfg?.mode === 'pinned' && typeof cfg.t === 'number'
          ? clamp(cfg.t, -0.92, 0.92)
          : offs[i];

        const p = portPoint(node, side, t);
        if (it.end === 'a') {
          it.e.aSide = side; it.e.aPt = p; it.e.aT = t;
          it.e.aEsc = escapePoint(p, side);
        } else {
          it.e.bSide = side; it.e.bPt = p; it.e.bT = t;
          it.e.bEsc = escapePoint(p, side);
        }
      });
    }
  }
}

/** Все занятые точки портов узла — для отрисовки засечек на корпусе. */
export function portsOfNode(s, devId) {
  const out = [];
  for (const e of s.edges.values()) {
    if (e.a === devId && e.aPt) out.push({ edge: e, end: 'a', pt: e.aPt, side: e.aSide, iface: e.aIface });
    if (e.b === devId && e.bPt) out.push({ edge: e, end: 'b', pt: e.bPt, side: e.bSide, iface: e.bIface });
  }
  return out;
}

/** Ближайшая допустимая позиция порта при ручном перетаскивании по периметру. */
export function nearestPortSlot(node, wx, wy) {
  const cands = [];
  for (const side of [SIDE_N, SIDE_E, SIDE_S, SIDE_W]) {
    const horizontal = side === SIDE_N || side === SIDE_S;
    const half = (horizontal ? node.w : node.h) / 2 || 1;
    const centerV = horizontal ? node.x + node.w / 2 : node.y + node.h / 2;
    const v = horizontal ? wx : wy;
    const t = clamp((v - centerV) / half, -0.92, 0.92);
    const p = portPoint(node, side, t);
    cands.push({ side, t, p, d: Math.hypot(p.x - wx, p.y - wy) });
  }
  cands.sort((a, b) => a.d - b.d);
  return cands[0];
}
